import React, { useCallback, useContext } from "react";
import { Transaction } from "@common/types/transaction";
import { TRANSFER_MATCH_ACTION } from "@common/requiredActions";
import { EagerLoaded } from "@common/types/base/orm";
import { Taxonomy } from "@common/categories";
import { AccrualMatchDisplay } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { useIsEntityAccrual } from "@hooks/useIsEntityAccrual";
import { SelectedAnswer } from "../TaxQuestions/types";
import { TransactionDetailsContext } from "./transactionDetails.context";
import { TransactionDetailsMemo } from "./TransactionDetailsMemo";
import { TransactionDetailsReceipt } from "./TransactionDetailsReceipt";
import { TransactionTaxQuestions } from "./TransactionTaxQuestions";
import { TransactionIgnoredMessage } from "./TransactionIgnoredMessage";
import { TransactionRequirementsIgnoredMessage } from "./TransactionRequirementsIgnoredMessage";
import { TransactionActionsMatchTransfer } from "./TransactionActions/TransactionActionMatchTransfer";
import { TransactionActionsAccrualMatch } from "./TransactionActions/TransactionActionAccrualMatch";
import { useApplicableTaxQuestions } from "./useApplicableTaxQuestions";

interface Props {
    transaction: EagerLoaded<
        Transaction,
        "transactionMatches" | "pairedTransfer" | "category"
    > & {
        accrualMatches?: AccrualMatchDisplay[];
    };
}

export const TransactionActionsSection: React.FC<Props> = ({ transaction }) => {
    const { onUpdate, memoRequired, receiptRequired } = useContext(
        TransactionDetailsContext,
    );
    const isEntityAccrual = useIsEntityAccrual(transaction.entityId);

    const transferMatchRequired = transaction.requiredActions.includes(
        TRANSFER_MATCH_ACTION,
    );

    const applicableTaxQuestions = useApplicableTaxQuestions(transaction);

    // For accrual entities the match section always renders (no taxonomy
    // gate) so the "required" indicator is purely about the four
    // taxonomies below; for accrual the section is just informational.
    const hasRequiredFields = Boolean(
        memoRequired ||
        receiptRequired ||
        applicableTaxQuestions.length > 0 ||
        (transferMatchRequired && !isEntityAccrual),
    );
    const hasOptionalFields = Boolean(!memoRequired || !receiptRequired);

    const onAnswer = useCallback(
        (answers: SelectedAnswer[]) => {
            void onUpdate({ taxQuestionAnswers: answers });
        },
        [onUpdate],
    );

    const shouldShowTransferMatch =
        !isEntityAccrual &&
        [
            Taxonomy.transfer,
            Taxonomy.contributions_distributions,
            Taxonomy.credit_card_payment,
            Taxonomy.personal,
        ].includes(transaction.category.taxonomy);

    return (
        <div className="transaction-details__tax-savings">
            {transaction.isIgnored && <TransactionIgnoredMessage />}
            {transaction.requirementsSnoozed &&
                !transaction.isIgnored &&
                transaction.isBusiness && (
                    <TransactionRequirementsIgnoredMessage />
                )}
            {hasRequiredFields && <div className="label mb-2">Required</div>}
            <TransactionTaxQuestions
                transaction={transaction}
                onAnswer={onAnswer}
                questions={applicableTaxQuestions}
            />
            {isEntityAccrual && (
                <TransactionActionsAccrualMatch transaction={transaction} />
            )}
            {shouldShowTransferMatch && (
                <TransactionActionsMatchTransfer
                    transaction={transaction}
                    required={transferMatchRequired}
                />
            )}
            {memoRequired && (
                <TransactionDetailsMemo
                    transaction={transaction}
                    required={!transaction.requirementsSnoozed}
                />
            )}
            {receiptRequired && (
                <TransactionDetailsReceipt
                    transaction={transaction}
                    required={!transaction.requirementsSnoozed}
                />
            )}

            {hasOptionalFields && <div className="label mb-2">Optional</div>}
            {!memoRequired && (
                <TransactionDetailsMemo
                    transaction={transaction}
                    required={false}
                />
            )}
            {!receiptRequired && (
                <TransactionDetailsReceipt
                    transaction={transaction}
                    required={false}
                />
            )}
        </div>
    );
};
